'use client'

import React from 'react'
import { Mail, Send, Upload, X } from 'lucide-react'
import type { ProcessedTheme, BrowserRow } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { CopyChip } from '../ProcessedEntry/DomainEntry/sharedComponents'
import { ResizableTable, TableTd, type ColumnDef } from '../ResizableTable'
import { EmailPillInput } from '../EmailPillInput'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import type { ActionableEvidence } from '../Entries/AdminSubmitEntryView'

// Generalized, domain-flavored sibling of SibsWidgets.tsx's SibsEmailWidget —
// same outbound relay + email_logs trail (see backend's new
// POST /campaigns/domain/dispatch), but for entries that resolve to a linked
// domain (CTT-style takedown reports) rather than SIBS/MBWAY fraud. The
// evidence-picker (a lazily-loaded pool of screenshot history you click to
// select into the outgoing email) is a trimmed-down port of CampaignForm.tsx's
// image gallery for CTT brand campaigns — same idea, but the images end up as
// email attachments instead of a public Trust Center gallery.
//
// Rendered only when the entry has a linked domain (CaseEntryDetailPage gates
// this on `domainId`), and works for every brand/tenant — there's no
// per-tenant recipients config yet, so `helpdesk.fraud@sibs.com` stays as the
// one hardcoded default (matching SibsEmailWidget) for tenantCode === 'sibs';
// every other tenant gets a blank, manually-filled "To" field until a real
// tenant_email_config table exists.
function defaultRecipientsForTenant(tenantCode?: string): string[] {
  if (tenantCode === 'sibs') return ['helpdesk.fraud@sibs.com']
  return []
}

type PoolImage = { id: string; label: string; base64: string; mime: string }
const THUMB_W = 88
const THUMB_H = 116

const LOG_COLUMNS: ColumnDef[] = [
  { key: 'date', label: 'Date', initialWidth: 90 },
  { key: 'to', label: 'To', initialWidth: 160 },
  { key: 'subject', label: 'Subject', initialWidth: 200 },
  { key: 'ref', label: 'Ref', initialWidth: 90 },
  { key: 'status', label: 'Status', initialWidth: 80 },
]

// Trimmed-down version of SibsWidgets.tsx's BODY_PREVIEW_COLUMNS — domain
// takedown reports don't carry the SIBS-specific entity/reference/sender/type
// fields, just what's relevant to a phishing domain — the backend's own email
// HTML table (POST /campaigns/domain/dispatch) mirrors this exact set.
const BODY_PREVIEW_COLUMNS: ColumnDef[] = [
  { key: 'date', label: 'Date', initialWidth: 90 },
  { key: 'domain', label: 'Domain', initialWidth: 160 },
  { key: 'platform', label: 'Platform', initialWidth: 110 },
  { key: 'brand_impersonation', label: 'Brand Impersonation', initialWidth: 130 },
  { key: 'has_evidence', label: 'Has Supporting Evidence', initialWidth: 160 },
]

function fmtDateShort(iso: string) {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-PT')
}

// Client-side mirror of the backend's defang() (campaignManagement.ts) —
// purely a preview so the operator sees exactly what the actually-sent email
// will look like; the backend re-defangs everything itself regardless of
// what's shown here, so this never needs to be the source of truth.
const URL_OR_DOMAIN_RE = /\b((?:https?:\/\/)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(?:\/[^\s"'<>]*)?)/gi
function defang(text: string | null | undefined): string | null {
  if (!text) return text ?? null
  return text.replace(URL_OR_DOMAIN_RE, (match) =>
    match.replace(/^https?:\/\//i, (m) => (m.toLowerCase().startsWith('https') ? 'hxxps://' : 'hxxp://')).replace(/\./g, '[.]')
  )
}

export type DomainTakedownEntry = {
  id: string
  user_entry_id: string | null
  created_at: string
  sender: string | null
  message_content: string | null
  entry_type?: string | null
  entity_reference_entity?: string | null
  entity_reference_reference?: string | null
  beneficiario?: string | null
  platform_text?: string | null
  platform_namelong?: string | null
}

// The operator's own words as the body's starting point: the evidence the
// pipeline extracted, in the investigator's language (PT-PT explanations),
// so a report is never sent with the bare table alone.
function bodyFromEvidence(ev: ActionableEvidence | null | undefined, domainUrl: string | null): string {
  if (!ev) return ''
  const lines: string[] = []
  if (ev.explanations.length) lines.push(...ev.explanations, '')
  if (domainUrl) lines.push(`Domínio: ${defang(domainUrl)}`)
  if (ev.phone_numbers.length) lines.push(`Contactos telefónicos usados: ${ev.phone_numbers.join(', ')}`)
  if (ev.emails.length) lines.push(`Endereços de email usados: ${ev.emails.join(', ')}`)
  if (ev.entity_references.length) lines.push(`Referências Multibanco: ${ev.entity_references.map((r) => `${r.entity_number ?? '?'}/${r.reference ?? '?'}${r.value ? ` (${r.value})` : ''}`).join(', ')}`)
  return lines.join('\n').trim()
}

export function DomainTakedownWidget({
  entry,
  userEntryId,
  evidence,
  domainUrl,
  browserRows,
  browserRowsLoading,
  tenantCode,
  theme,
  getToken,
  onSent,
}: {
  entry: DomainTakedownEntry
  // With a user_entry_id the send is recorded as a tracked custom_email
  // action (replies come back to it, the Actions widget lists it under
  // "Other actions"); without one it is the plain dispatch it always was.
  userEntryId?: string | null
  evidence?: ActionableEvidence | null
  domainUrl: string | null
  browserRows: BrowserRow[]
  browserRowsLoading?: boolean
  tenantCode?: string
  theme: ProcessedTheme
  getToken: () => Promise<string>
  onSent?: () => void
}) {
  const c = watchClasses(theme)
  const isDark = theme === 'dark'

  const defaultSubject = React.useMemo(() => {
    return `Domain Detection Report · ${defang(domainUrl) ?? defang(entry.sender) ?? 'Unknown'}`
  }, [entry, domainUrl])

  const [to, setTo] = React.useState<string[]>(() => defaultRecipientsForTenant(tenantCode))
  const [bcc, setBcc] = React.useState<string[]>(() => process.env.NEXT_PUBLIC_ALERTS_BCC_EMAIL ? [process.env.NEXT_PUBLIC_ALERTS_BCC_EMAIL] : [])
  const [subject, setSubject] = React.useState(defaultSubject)
  const [body, setBody] = React.useState(() => bodyFromEvidence(evidence, domainUrl))
  const [brandImpersonation, setBrandImpersonation] = React.useState(false)
  // The table's own cells, editable independent of the entry/domain data
  // they default from — an operator may need to correct/adjust what's
  // actually reported (e.g. the domain the recipient should look at isn't
  // always exactly originalUrl) without that meaning anything else about
  // the entry itself. Sent to the backend as explicit overrides rather than
  // re-derived from `entry`/`domainUrl` there.
  const [tableDate, setTableDate] = React.useState(() => fmtDateShort(entry.created_at))
  const [tableDomain, setTableDomain] = React.useState(() => defang(domainUrl) ?? '')
  const [tablePlatform, setTablePlatform] = React.useState(() => entry.platform_text ?? entry.platform_namelong ?? '')
  const [pool, setPool] = React.useState<PoolImage[]>([])
  const [selected, setSelected] = React.useState<PoolImage[]>([])
  const [loadingR2Key, setLoadingR2Key] = React.useState<string | null>(null)
  const [sending, setSending] = React.useState(false)
  const [result, setResult] = React.useState<{ ok: boolean; msg: string } | null>(null)
  const [logs, setLogs] = React.useState<any[]>([])
  const [logsLoading, setLogsLoading] = React.useState(false)

  // Tracks the last domainUrl this widget has already synced its
  // domain-derived defaults from, per entry — reset to null whenever the
  // entry itself changes below.
  const appliedDomainUrlRef = React.useRef<string | null | undefined>(undefined)

  React.useEffect(() => {
    appliedDomainUrlRef.current = undefined
    setTo(defaultRecipientsForTenant(tenantCode))
    setSubject(defaultSubject)
    setBody('')
    setBrandImpersonation(false)
    setTableDate(fmtDateShort(entry.created_at))
    setTableDomain(defang(domainUrl) ?? '')
    setTablePlatform(entry.platform_text ?? entry.platform_namelong ?? '')
    setPool([])
    setSelected([])
    setResult(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id])

  // `domainUrl` comes from a separate async fetch (CaseEntryDetailPage's own
  // domainData) that routinely resolves after this widget has already
  // mounted with domainUrl still null — the reset effect above only fires on
  // entry.id, so without this, the subject/table Domain cell get stuck at
  // their pre-load "Unknown"/empty defaults for the rest of the entry's
  // session. Syncs once per (entry, domainUrl) pair rather than every
  // domainUrl reference change, and only touches the two domain-derived
  // fields so it doesn't clobber body/images/brandImpersonation if the
  // operator already started filling those in during the load window.
  React.useEffect(() => {
    if (!domainUrl || appliedDomainUrlRef.current === domainUrl) return
    appliedDomainUrlRef.current = domainUrl
    setSubject(`Domain Detection Report · ${defang(domainUrl) ?? 'Unknown'}`)
    setTableDomain(defang(domainUrl) ?? '')
  }, [domainUrl])

  const fetchLogs = React.useCallback(() => {
    setLogsLoading(true)
    fetch(`/api/campaigns/logs/${entry.id}?campaign=domain_detection_dispatch`)
      .then((r) => r.json())
      .then((payload) => { if (payload?.success) setLogs(payload.data ?? []) })
      .finally(() => setLogsLoading(false))
  }, [entry.id])

  React.useEffect(() => { fetchLogs() }, [fetchLogs])

  const addToPool = React.useCallback((item: PoolImage) => {
    setPool((prev) => (prev.some((p) => p.id === item.id) ? prev : [...prev, item]))
  }, [])

  const toggleSelected = (item: PoolImage) => {
    setSelected((prev) => (prev.some((p) => p.id === item.id) ? prev.filter((p) => p.id !== item.id) : [...prev, item]))
  }

  const unloadedRows = browserRows.slice(0, 3).filter((r) => r.r2Key && !pool.some((p) => p.id.startsWith(r.r2Key!)))

  const handleLoadScreenshot = async (r2Key: string, dateLabel: string) => {
    if (loadingR2Key) return
    setLoadingR2Key(r2Key)
    try {
      const token = await getToken()
      const res = await fetch('/api/screenshots/bykey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ r2Key, turnstileToken: token }),
      })
      const json = await res.json()
      if (!json.success) { setResult({ ok: false, msg: 'Failed to load screenshot.' }); return }
      const scenarios = json.data?.data?.buffer?.scenarios
      const dB64 = scenarios?.desktop?.screenshots?.[0]?.data ?? null
      const mB64 = scenarios?.mobile?.screenshots?.[0]?.data ?? null
      if (dB64) addToPool({ id: `${r2Key}-desktop`, base64: dB64, mime: 'image/png', label: `${dateLabel} · Desktop` })
      if (mB64) addToPool({ id: `${r2Key}-mobile`, base64: mB64, mime: 'image/png', label: `${dateLabel} · Mobile` })
    } catch {
      setResult({ ok: false, msg: 'Network error loading screenshot.' })
    } finally {
      setLoadingR2Key(null)
    }
  }

  const readFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string
      const [header, base64] = dataUrl.split(',')
      const mime = header.match(/data:([^;]+)/)?.[1] ?? 'image/png'
      const item: PoolImage = { id: `upload-${Date.now()}`, base64, mime, label: file.name || 'Upload' }
      addToPool(item)
      setSelected((prev) => [...prev, item])
    }
    reader.readAsDataURL(file)
  }

  const isValid = to.length > 0 && subject.trim().length > 0
  // Domain data (domainUrl/tableDomain) loads separately from the entry
  // itself and can still be in flight right when this widget first mounts —
  // this drives both the "resolving domain…" header note and the Domain
  // cell's own loading placeholder below, so that window reads as loading
  // rather than as a blank/broken field.
  const domainStillLoading = browserRowsLoading && !tableDomain

  const send = async () => {
    if (!isValid || sending) return
    setSending(true)
    setResult(null)
    try {
      const images = selected.map((s) => ({ base64: s.base64, mime: s.mime, label: s.label }))
      const res = userEntryId
        ? await fetch(`/api/entries/actions/entry/${encodeURIComponent(userEntryId)}/custom-email`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ to, bcc: bcc.length > 0 ? bcc : undefined, subject, body, images, brand_impersonation: brandImpersonation }),
          })
        : await fetch('/api/campaigns/domain/dispatch', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              entry,
              domainUrl,
              subject,
              body,
              to: to.join(','),
              bcc: bcc.length > 0 ? bcc.join(',') : undefined,
              brandImpersonation,
              tableDate,
              tableDomain,
              tablePlatform,
              tenantCode,
              images,
            }),
          })
      const payload = await res.json().catch(() => null)
      if (payload?.success) {
        setResult({ ok: true, msg: userEntryId ? 'Sent as a tracked report — see the Actions taken rows above.' : 'Report dispatched successfully.' })
        setBody('')
        setSelected([])
        fetchLogs()
        onSent?.()
      } else {
        setResult({ ok: false, msg: payload?.error ?? 'Dispatch failed.' })
      }
    } catch {
      setResult({ ok: false, msg: 'Network error.' })
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>
        <Mail className="h-3 w-3 shrink-0" />
        Domain detection report · manual sender
        {/* domainUrl loads from a separate fetch than the entry itself, so
            there's a real window right after opening this widget where it's
            still null — without this, the subject/table Domain cell just
            looked permanently blank/"Unknown" instead of visibly loading. */}
        {domainStillLoading && (
          <span className="animate-pulse text-signal">· resolving domain…</span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_2fr]">
        <label className="flex flex-col gap-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>To</span>
          <EmailPillInput
            value={to}
            onChange={setTo}
            theme={theme}
            placeholder={tenantCode ? `No default for tenant "${tenantCode}" yet — enter manually` : 'recipient@example.com'}
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>BCC</span>
          <EmailPillInput value={bcc} onChange={setBcc} theme={theme} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Subject</span>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={`h-7 text-[11px] px-2 ${''}`}
          />
        </label>
      </div>

      <label className="flex flex-col gap-0.5">
        <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Body <span className={'text-subtle'}>(optional)</span></span>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          placeholder="Describe the takedown request…"
          className={`text-[12px] px-2 py-1.5 ${''}`}
        />
      </label>

      {/* Same entity-reference table shape as the SIBS dispatch email
          (Date/Domain/Platform/Brand Impersonation) — this is a preview of
          the table the backend actually renders into the email HTML — every
          cell here is editable and sent to the backend as an explicit
          override rather than re-derived from `entry`/`domainUrl` there, so
          what you see here is exactly what gets sent. Domain defaults to the
          defanged value (hxxp://, [.]) so it isn't an accidentally
          clickable/scannable live link, but it's plain text once you start
          editing it — re-defang it yourself if you paste a live domain back
          in. */}
      <div>
        <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Body preview · defanged · editable</div>
        <div className={`overflow-x-auto rounded border ${c.line}`}>
          <ResizableTable
            columns={BODY_PREVIEW_COLUMNS}
            tableClassName="w-full border-collapse text-[11px]"
            thRowClassName={`border-b font-mono text-[10px] uppercase tracking-widest border-border bg-secondary text-muted-foreground`}
            thPadding="px-2 py-1"
          >
            <tr className={'bg-card'}>
              <TableTd dense className="whitespace-nowrap">
                <input
                  value={tableDate}
                  onChange={(e) => setTableDate(e.target.value)}
                  className={`w-full min-w-0 rounded-sm bg-transparent font-mono text-[11px] outline-none focus:ring-1 focus:ring-ring ${c.faint}`}
                />
              </TableTd>
              <TableTd dense className="whitespace-nowrap">
                {domainStillLoading ? (
                  <span className={`block animate-pulse font-mono text-[11px] ${c.faint}`}>Loading…</span>
                ) : (
                  <input
                    value={tableDomain}
                    onChange={(e) => setTableDomain(e.target.value)}
                    className={`w-full min-w-0 rounded-sm bg-transparent font-mono text-[11px] outline-none focus:ring-1 focus:ring-ring ${c.muted}`}
                  />
                )}
              </TableTd>
              <TableTd dense className="whitespace-nowrap">
                <input
                  value={tablePlatform}
                  onChange={(e) => setTablePlatform(e.target.value)}
                  className={`w-full min-w-0 rounded-sm bg-transparent font-mono text-[11px] outline-none focus:ring-1 focus:ring-ring ${c.faint}`}
                />
              </TableTd>
              <TableTd dense className="whitespace-nowrap">
                <label className={`flex items-center gap-1.5 cursor-pointer font-mono text-[10px] uppercase tracking-widest ${brandImpersonation ? 'text-destructive' : c.faint}`}>
                  <input
                    type="checkbox"
                    checked={brandImpersonation}
                    onChange={(e) => setBrandImpersonation(e.target.checked)}
                    className="h-3 w-3 cursor-pointer accent-red-500"
                  />
                  {brandImpersonation ? 'Yes' : 'No'}
                </label>
              </TableTd>
              <TableTd dense className="whitespace-nowrap">
                {/* Derived from the Evidence images section below, not
                    independently editable — it'd be misleading for this to
                    disagree with whether any images are actually attached. */}
                <span className={`font-mono text-[10px] uppercase tracking-widest ${selected.length > 0 ? 'text-signal' : c.faint}`}>
                  {selected.length > 0 ? `Yes · ${selected.length}` : 'No'}
                </span>
              </TableTd>
            </tr>
          </ResizableTable>
        </div>
      </div>

      {/* Evidence images — same pick-from-screenshot-history pattern as the
          CTT brand campaign composer (CampaignForm.tsx), trimmed to just the
          pool + selection toggle since there's no gallery ordering to manage
          for an email's attachments. */}
      <div className={`rounded border p-2 ${c.line}`}>
        <div className={`font-mono text-[9px] uppercase tracking-widest mb-1.5 ${c.faint}`}>
          Evidence images · <span className="text-signal">{selected.length} selected</span>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {pool.map((item) => {
            const isSelected = selected.some((s) => s.id === item.id)
            return (
              <div
                key={item.id}
                onClick={() => toggleSelected(item)}
                role="button"
                className={`relative shrink-0 cursor-pointer select-none overflow-hidden rounded border-2 transition-all ${isSelected ? 'border-signal' : 'border-transparent hover:border-signal/50'}`}
                style={{ width: THUMB_W }}
              >
                <div className={`overflow-hidden bg-secondary`} style={{ height: THUMB_H }}>
                  <img src={`data:${item.mime};base64,${item.base64}`} alt={item.label} className="h-full w-full object-cover" />
                </div>
                <div className={`truncate border-t px-1 py-0.5 text-center font-mono text-[7px] ${c.line} ${isSelected ? 'text-signal' : c.faint}`}>
                  {item.label}
                </div>
              </div>
            )
          })}

          {browserRowsLoading && (
            <div className={`flex shrink-0 animate-pulse items-center justify-center rounded border-2 border-dashed font-mono text-[8px] ${c.line} ${c.faint}`} style={{ width: THUMB_W, height: THUMB_H + 18 }}>
              Loading…
            </div>
          )}
          {unloadedRows.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => handleLoadScreenshot(r.r2Key!, fmtDateShort(r.createdAt))}
              disabled={loadingR2Key === r.r2Key}
              className={`flex shrink-0 flex-col items-center justify-center gap-1 rounded border-2 border-dashed font-mono text-[8px] transition-colors hover:border-signal hover:text-signal disabled:opacity-50 ${c.line} ${c.faint}`}
              style={{ width: THUMB_W, height: THUMB_H + 18 }}
            >
              <span>{loadingR2Key === r.r2Key ? '⟳' : '+'}</span>
              <span className="px-1 text-center leading-tight">{fmtDateShort(r.createdAt)}</span>
            </button>
          ))}

          <label className="block shrink-0 cursor-pointer" style={{ width: THUMB_W, height: THUMB_H + 18 }}>
            <div
              className={`flex h-full flex-col items-center justify-center gap-1 rounded border-2 border-dashed transition-colors hover:border-signal hover:bg-secondary border-border`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.type.startsWith('image/')) readFile(f) }}
            >
              <Upload className={`h-3.5 w-3.5 ${c.faint}`} />
              <span className={`font-mono text-[7px] uppercase tracking-widest ${c.faint}`}>Upload</span>
            </div>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) readFile(f) }} />
          </label>
        </div>

        {selected.length > 0 && (
          <div className={`mt-2 flex flex-wrap gap-1.5 border-t pt-2 ${c.line}`}>
            {selected.map((img) => (
              <span key={img.id} className={`flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[9px] ${c.line} ${c.faint}`}>
                {img.label}
                <button type="button" onClick={() => setSelected((prev) => prev.filter((p) => p.id !== img.id))} className="hover:text-destructive">
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {result && (
        <div className={`rounded border px-2 py-1 font-mono text-[10px] ${result.ok ? 'border-success/40 bg-success-surface text-success' : 'border-destructive/40 bg-danger-surface text-destructive'}`}>
          {result.msg}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          size="sm"
          className="h-7 gap-1.5 px-3 font-mono text-[10px] uppercase tracking-widest"
          disabled={!isValid || sending || domainStillLoading}
          onClick={send}
        >
          <Send className="h-3 w-3" />
          {domainStillLoading ? 'Resolving domain…' : sending ? 'Sending…' : 'Send'}
        </Button>
      </div>

      <div className={`mt-1 border-t pt-2 ${c.line}`}>
        <div className="mb-1.5 flex items-center justify-between">
          <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Email log</span>
          <button onClick={fetchLogs} className={`font-mono text-[9px] uppercase tracking-widest hover:text-signal ${c.faint}`}>refresh</button>
        </div>
        {logsLoading ? (
          <p className={`animate-pulse font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>Loading…</p>
        ) : logs.length === 0 ? (
          <p className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>No reports sent yet</p>
        ) : (
          <div className={`overflow-x-auto rounded border ${c.line}`}>
            <ResizableTable
              columns={LOG_COLUMNS}
              tableClassName="w-full border-collapse text-[11px]"
              thRowClassName={`border-b font-mono text-[10px] uppercase tracking-widest border-border bg-secondary text-muted-foreground`}
              thPadding="px-2 py-1"
            >
              {logs.map((log) => (
                <tr key={log.id} className={`border-b last:border-0 ${c.line}`}>
                  <TableTd dense className={`whitespace-nowrap font-mono ${c.faint}`}>{fmtDateShort(log.created_at)}</TableTd>
                  <TableTd dense className={`whitespace-nowrap font-mono ${c.muted}`}>{(log.to ?? []).join(', ')}</TableTd>
                  <TableTd dense className={`max-w-[200px] truncate ${c.muted}`}>{log.subject}</TableTd>
                  <TableTd dense className="whitespace-nowrap">{log.id ? <CopyChip value={log.id} display={log.id.slice(0, 8)} /> : '—'}</TableTd>
                  <TableTd dense className="whitespace-nowrap">
                    <span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${log.status === 'sent' ? 'border-success/40 bg-success-surface text-success' : 'border-destructive/40 bg-danger-surface text-destructive'}`}>
                      {log.status}
                    </span>
                  </TableTd>
                </tr>
              ))}
            </ResizableTable>
          </div>
        )}
      </div>
    </div>
  )
}
